import { Injectable } from '@angular/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import { Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { DeviceData, DeviceOperatingSystem, DeviceType } from './device-detection.model';

const BP_MOBILE_MAX  = '(max-width: 767.98px)';
const BP_TABLET_MIN  = '(min-width: 768px)';
const BP_TABLET_MAX  = '(max-width: 1023.98px)';
const BP_DESKTOP_MIN = '(min-width: 1024px)';

@Injectable({
  providedIn: 'root',
})
export class DeviceDetectionService {
  private readonly deviceData$: Observable<DeviceData>;

  constructor(private readonly breakpointObserver: BreakpointObserver) {
    this.deviceData$ = this.breakpointObserver
      .observe([
        BP_MOBILE_MAX,
        BP_TABLET_MIN,
        BP_TABLET_MAX,
        BP_DESKTOP_MIN,
      ])
      .pipe(
        map((result) => {
          const os = this.detectOS();
          const type = this.detectDeviceType(result.breakpoints, os);

          return { type, os };
        }),
        shareReplay(1)
      );
  }

  getDeviceData$(): Observable<DeviceData> {
    return this.deviceData$;
  }

  private detectOS(): DeviceOperatingSystem {
    const userAgent = navigator.userAgent;

    if (/iPad|iPhone|iPod/.test(userAgent)) {
      return DeviceOperatingSystem.iOS;
    }

    if (/android/i.test(userAgent)) {
      return DeviceOperatingSystem.Android;
    }

    if (/Win/i.test(userAgent)) {
      return DeviceOperatingSystem.Windows;
    }

    if (/Mac/i.test(userAgent)) {
      return DeviceOperatingSystem.MacOS;
    }

    if (/Linux/i.test(userAgent)) {
      return DeviceOperatingSystem.Linux;
    }

    return DeviceOperatingSystem.Unknown;
  }

  private detectDeviceType(breakpoints: { [key: string]: boolean }, os: DeviceOperatingSystem): DeviceType {
    if (os === DeviceOperatingSystem.Windows ||
        os === DeviceOperatingSystem.MacOS ||
        os === DeviceOperatingSystem.Linux) {
      return DeviceType.Desktop;
    }

    if (os === DeviceOperatingSystem.iOS) {
      return /iPad/.test(navigator.userAgent) ? DeviceType.Tablet : DeviceType.Mobile;
    }

    if (breakpoints[BP_MOBILE_MAX]) {
      return DeviceType.Mobile;
    }

    if (breakpoints[BP_TABLET_MIN] && breakpoints[BP_TABLET_MAX]) {
      return DeviceType.Tablet;
    }

    return DeviceType.Desktop;
  }
}
